import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import tipotLogo from 'assets/images/tipot-logo.png';
import { ReactComponent as HomeIcon } from 'assets/images/home.svg';
import { ReactComponent as SearchIcon } from 'assets/images/search.svg';
import { ReactComponent as MyPageIcon } from 'assets/images/stars.svg';
import { ReactComponent as NotificationIcon } from 'assets/images/bell.svg';
import { ReactComponent as SettingIcon } from 'assets/images/settings.svg';
import { useQuery } from 'react-query';
import { getUnreadNotificationCount } from 'apis';
import { useState } from 'react';

const authLinks = [
    { href: '/', title: '홈', icon: HomeIcon },
    { href: '/explore', title: '둘러보기', icon: SearchIcon },
    { href: '/mypage', title: '마이페이지', icon: MyPageIcon },
    { href: '/notification', title: '알림', icon: NotificationIcon, notificationCount: 0 },
    { href: '/setting', title: '설정', icon: SettingIcon },
];

interface SideBarProps {
    isMobile?: boolean;
}

const SideBar = ({ isMobile = false }: SideBarProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isLoggedIn = !!localStorage.getItem('user_access_token') && !!localStorage.getItem('terms_agree');

    const [navLinks, setNavLinks] = useState(authLinks);

    useQuery('sideBarLinks', getUnreadNotificationCount, {
        enabled: isLoggedIn,
        onSuccess: (data) => {
            setNavLinks((prev) =>
                prev.map((link) =>
                    link.href === '/notification'
                        ? { ...link, notificationCount: data.data.data || 0 }
                        : link
                )
            );
        },
    });

    // ──────── PUBLIC NAV (not logged in) ────────
    if (!isLoggedIn) {
        if (isMobile) {
            return (
                <div className="flex justify-around items-center w-full py-2 safe-area-bottom">
                    <NavLink to="/" className="flex flex-col items-center gap-0.5 py-1 px-3">
                        <HomeIcon className="w-5 h-5" style={{ stroke: location.pathname === '/' ? '#111827' : '#9CA3AF' }} />
                        <span className={`text-[10px] ${location.pathname === '/' ? 'font-bold text-achromatic-900' : 'font-medium text-achromatic-400'}`}>
                            홈
                        </span>
                    </NavLink>
                    <NavLink to="/explore" className="flex flex-col items-center gap-0.5 py-1 px-3">
                        <SearchIcon className="w-5 h-5" style={{ stroke: location.pathname.startsWith('/explore') ? '#111827' : '#9CA3AF' }} />
                        <span className={`text-[10px] ${location.pathname.startsWith('/explore') ? 'font-bold text-achromatic-900' : 'font-medium text-achromatic-400'}`}>
                            둘러보기
                        </span>
                    </NavLink>
                    <NavLink to="/login" className="flex flex-col items-center gap-0.5 py-1 px-3">
                        <MyPageIcon className="w-5 h-5" style={{ stroke: '#9CA3AF' }} />
                        <span className="text-[10px] font-medium text-achromatic-400">로그인</span>
                    </NavLink>
                </div>
            );
        }

        return (
            <nav>
                <div className="flex items-center h-[60px] justify-between">
                    {/* Left: Logo + Search */}
                    <div className="flex items-center gap-5">
                        <NavLink to="/" className="flex-shrink-0 flex items-center gap-2">
                    <img src={tipotLogo} alt="Tipot" className="w-[36px] h-[36px] object-contain" />
                    <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, letterSpacing: '-0.5px' }} className="text-[20px] text-gray-900">Ti<span className="text-indigo-500">Pot</span></span>
                        </NavLink>
                        <div
                            className="flex items-center gap-2 bg-achromatic-50 rounded-xl px-4 py-2.5 w-[240px] cursor-pointer hover:bg-achromatic-100/80 transition-colors"
                            onClick={() => navigate('/explore')}
                        >
                            <SearchIcon className="w-4 h-4" style={{ stroke: '#9CA3AF' }} />
                            <span className="text-[13px] text-achromatic-400">서비스 검색...</span>
                        </div>
                    </div>

                    {/* Right: Nav Links + CTA */}
                    <div className="flex items-center gap-1.5">
                        <NavLink
                            to="/explore"
                            className={`px-3.5 py-2 rounded-lg text-[13px] font-semibold transition-colors ${
                                location.pathname.startsWith('/explore')
                                    ? 'text-achromatic-900'
                                    : 'text-achromatic-500 hover:text-achromatic-700'
                            }`}
                        >
                            둘러보기
                        </NavLink>
                        <NavLink
                            to="/login"
                            className="px-4 py-2 bg-primary-500 text-white rounded-lg text-[13px] font-bold hover:bg-primary-600 transition-colors"
                        >
                            시간 팔기
                        </NavLink>
                        <NavLink
                            to="/login"
                            className="px-3.5 py-2 text-[13px] font-semibold text-achromatic-500 hover:text-achromatic-700 transition-colors"
                        >
                            로그인
                        </NavLink>
                    </div>
                </div>
            </nav>
        );
    }

    // ──────── AUTH NAV - MOBILE BOTTOM ────────
    if (isMobile) {
        return (
            <div className="flex justify-around items-center w-full py-2 safe-area-bottom">
                {navLinks.map((link, index) => {
                    const Icon = link.icon;
                    const isHomeLink = link.href === '/';
                    const isActive = isHomeLink
                        ? location.pathname === link.href
                        : location.pathname.startsWith(link.href);

                    return (
                        <NavLink
                            key={index}
                            to={link.href}
                            className="flex flex-col items-center gap-0.5 py-1 px-2 min-w-[48px]"
                        >
                            <div className="relative">
                                <Icon
                                    className="w-[22px] h-[22px]"
                                    style={{ stroke: isActive ? '#111827' : '#9CA3AF' }}
                                />
                                {(link.notificationCount ?? 0) > 0 && (
                                    <span className="absolute -top-0.5 -right-1.5 min-w-[14px] h-[14px] bg-red-500 rounded-full flex items-center justify-center">
                                        <span className="text-[8px] font-bold text-white leading-none">
                                            {(link.notificationCount ?? 0) > 9 ? '9+' : link.notificationCount}
                                        </span>
                                    </span>
                                )}
                            </div>
                            <span
                                className={`text-[10px] ${
                                    isActive ? 'font-bold text-achromatic-900' : 'font-medium text-achromatic-400'
                                }`}
                            >
                                {link.title}
                            </span>
                        </NavLink>
                    );
                })}
            </div>
        );
    }

    // ──────── AUTH NAV - DESKTOP TOP ────────
    return (
        <nav>
            <div className="flex items-center h-[60px] gap-1">
                <NavLink to="/" className="mr-5 flex-shrink-0 flex items-center gap-2">
                    <img src={tipotLogo} alt="Tipot" className="w-[36px] h-[36px] object-contain" />
                    <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, letterSpacing: '-0.5px' }} className="text-[20px] text-gray-900">Ti<span className="text-indigo-500">Pot</span></span>
                </NavLink>
                <div className="flex items-center gap-0.5 flex-1">
                    {navLinks.map((link, index) => {
                        const Icon = link.icon;
                        const isHomeLink = link.href === '/';
                        const isActive = isHomeLink
                            ? location.pathname === link.href
                            : location.pathname.startsWith(link.href);

                        return (
                            <NavLink
                                key={index}
                                to={link.href}
                                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-semibold transition-all duration-150 ${
                                    isActive
                                        ? 'text-achromatic-900'
                                        : 'text-achromatic-400 hover:text-achromatic-600'
                                }`}
                            >
                                <div className="relative">
                                    <Icon
                                        className="w-[16px] h-[16px]"
                                        style={{ stroke: isActive ? '#111827' : '#9CA3AF' }}
                                    />
                                    {(link.notificationCount ?? 0) > 0 && (
                                        <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-red-500 rounded-full" />
                                    )}
                                </div>
                                <span>{link.title}</span>
                            </NavLink>
                        );
                    })}
                </div>
            </div>
        </nav>
    );
};

export default SideBar;
